'use client';

import {
  faBrainCircuit,
  faCommentPlus,
  faMemoPad,
  faMessagesQuestion,
  faThumbsUp,
} from '@fortawesome/pro-duotone-svg-icons';
import { faChevronsRight, faChevronsLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import SidebarSection from '../SidebarSection';
import AiChatModal from './AiChatModal';
import { getChats } from '@/api/requests/chats';
import SubscriptionLimitsCard from '@/components/subscription/SubscriptionLimitsCard';
import { CommentSection } from '@/components/comments/CommentSection';
import { Chat } from '@/actions/dtos/chat';
import { useAccount } from '@/hooks/useAccount';
import { useSubscriptionLimits } from '@/hooks/useSubscriptionLimits';
import { useTenderData } from '@/hooks/useTenderData';
import log from '@/utils/logger';
import { useDispatch } from 'react-redux';
import { openModal, FeatureType } from '@/slices/features/modalSlice';
import Button from '@/components/common/Button';
import { TenderResult } from '@/actions/dtos/tender/tender';
import { getSourcePath } from '@/rewrites';
import Link from 'next/link';
import TeamVotesSection from './TeamVotesSection';
import { useTranslations } from 'next-intl';

interface TenderSidebarProps {
  tenderId: string;
  isActive: boolean;
  hasDocuments: boolean;
  quickCopyText: string;
  isAuthenticated: boolean;
  isLargePanelVisible: boolean;
  setIsLargePanelVisible: (arg0: boolean) => void;
  activeSection?: string | null;
  onSectionSelected?: () => void;
  tenderResults?: TenderResult[];
}

const TenderSidebar: React.FC<TenderSidebarProps> = ({
  tenderId,
  isActive,
  hasDocuments,
  isLargePanelVisible,
  setIsLargePanelVisible,
  activeSection,
  onSectionSelected,
  tenderResults,
}) => {
  const dispatch = useDispatch();
  const [isSmallPanelVisible, setIsSmallPanelVisible] = useState<boolean>(true);
  const [isAnimatingSmallPanel, setIsAnimatingSmallPanel] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    kiChats: false,
    teamVotes: false,
    notesAndComments: false,
    kiScore: false,
  });
  const [recentChats, setRecentChats] = useState<Chat[]>([]);
  const [chatId, setChatId] = useState<string>('');
  const { isQuestionLimitReached, showQuestionLimitCard } = useSubscriptionLimits();
  const { organization } = useAccount();
  const { getVotesByTenderId } = useTenderData(tenderId, { includeVotes: true });
  const modalRef = useRef<HTMLDivElement | null>(null);
  const aiChatRef = useRef<boolean>(false);
  const t = useTranslations();

  const [topOffset, setTopOffset] = useState(112);

  const votes = getVotesByTenderId(tenderId);

  const openLargePanel = () => {
    setIsAnimatingSmallPanel(true);
    setTimeout(() => {
      setIsSmallPanelVisible(false);
      setIsAnimatingSmallPanel(false);
      setIsLargePanelVisible(true);
    }, 300);
  };

  const closeLargePanel = useCallback(() => {
    setIsLargePanelVisible(false);
    setTimeout(() => {
      setIsSmallPanelVisible(true);
    }, 300);
  }, [setIsLargePanelVisible, setIsSmallPanelVisible]);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node) && !aiChatRef.current) {
        const isScrollbarClick = event.clientX > document.documentElement.clientWidth - 20;
        if (!isScrollbarClick) {
          closeLargePanel();
        }
      }
    },
    [closeLargePanel],
  );

  const handleOpenChat = (chatId: string = '') => {
    if (isQuestionLimitReached) {
      dispatch(openModal(FeatureType.MORE_CHAT_MESSAGES));
      return;
    }

    setChatId(chatId);
    setIsModalOpen(true);
    aiChatRef.current = true;
  };

  useEffect(() => {
    if (!isModalOpen) {
      aiChatRef.current = false;
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (isLargePanelVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, isLargePanelVisible]);

  useEffect(() => {
    const fetchChats = async () => {
      if (!organization || !isLargePanelVisible) return;
      try {
        const response = await getChats(tenderId, organization.id);
        const sortedRecentChats = response
          .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
          .slice(0, 5);
        setRecentChats(sortedRecentChats);
      } catch (error) {
        log.error('Failed to fetch recent chats:', error);
      }
    };

    fetchChats();
  }, [tenderId, organization, chatId, isLargePanelVisible]);

  const toggleSection = useCallback((section: keyof typeof openSections) => {
    setOpenSections((prevSections) => {
      const newState = { ...prevSections };
      for (const key in newState) {
        if (key === section) {
          newState[key] = !prevSections[section];
        } else {
          newState[key] = false;
        }
      }
      return newState;
    });
  }, []);

  const handleIconClick = (section: string) => {
    openLargePanel();
    if (!openSections[section as keyof typeof openSections]) {
      toggleSection(section as keyof typeof openSections);
    }
  };

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          let dynamicOffset;
          if (scrollY > 49) {
            dynamicOffset = 64;
          } else {
            dynamicOffset = Math.max(0, 112 - scrollY);
          }

          setTopOffset(dynamicOffset);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (activeSection && isLargePanelVisible && !openSections[activeSection as keyof typeof openSections]) {
      toggleSection(activeSection as keyof typeof openSections);
      if (onSectionSelected) {
        onSectionSelected();
      }
    }
  }, [activeSection, isLargePanelVisible, toggleSection, onSectionSelected, openSections]);

  return (
    <>
      {(isSmallPanelVisible || isAnimatingSmallPanel) && (
        <div
          className={`hidden sm:fixed sm:right-0 sm:top-0 sm:z-20 sm:flex sm:h-full sm:w-auto 
            sm:flex-col sm:items-stretch sm:justify-start sm:gap-0 sm:border-l sm:transition-transform sm:duration-300 sm:ease-in-out
            ${isAnimatingSmallPanel ? 'sm:translate-x-full' : 'sm:translate-x-0'}`}
          style={{ top: `${topOffset}px` }}
        >
          <FontAwesomeIcon
            icon={faChevronsLeft}
            onClick={() => handleIconClick('kiChats')}
            className="my-4 cursor-pointer text-sm text-[#A1A6B7]"
          />

          <button
            className="flex h-14 w-14 items-center justify-center"
            aria-label="Toggle Menu"
            onClick={() => handleIconClick('kiScore')}
          >
            <FontAwesomeIcon className="h-7 w-7" icon={faBrainCircuit} />
          </button>

          <button
            className="relative flex h-14 w-14 items-center justify-center"
            aria-label="Toogle Menu"
            onClick={() => handleIconClick('teamVotes')}
          >
            <FontAwesomeIcon className={`h-7 w-7`} icon={faThumbsUp} />
          </button>

          {isActive && hasDocuments && (
            <button
              className="flex h-14 w-14 items-center justify-center"
              aria-label="Toggle Menu"
              onClick={() => handleIconClick('kiChats')}
            >
              <FontAwesomeIcon className="h-7 w-7" icon={faMessagesQuestion} />
            </button>
          )}

          <button
            className="flex h-14 w-14 items-center justify-center"
            aria-label="Toggle Menu"
            onClick={() => handleIconClick('notesAndComments')}
          >
            <FontAwesomeIcon className="h-7 w-7" icon={faMemoPad} />
          </button>
        </div>
      )}

      <div
        className={`fixed right-0 z-20 h-full w-full transform bg-base-100 transition-transform duration-300 
          sm:w-[20.5rem] sm:border-l ${isLargePanelVisible ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ top: `${topOffset}px` }}
        ref={modalRef}
      >
        <div className="p-4">
          <FontAwesomeIcon icon={faChevronsRight} onClick={closeLargePanel} className="cursor-pointer text-[#A1A6B7]" />

          <div className="mt-4 flex flex-col gap-4">
            <SidebarSection
              title="KI Bewertungen"
              icon={faBrainCircuit}
              isOpen={openSections.kiScore}
              toggle={() => toggleSection('kiScore')}
            >
              {tenderResults && tenderResults.length > 0 ? (
                tenderResults.map((value) => (
                  <Link
                    key={value.id}
                    className="flex justify-between rounded-lg border border-base-100 bg-base-200 p-2 hover:border-primary"
                    href={getSourcePath('/search') + '/' + value.execution.id}
                  >
                    <span className="mr-2 line-clamp-3 text-sm">{value.execution.query.description || ''}</span>
                    <span className="text-sm">{value.score}%</span>
                  </Link>
                ))
              ) : (
                <span className="text-sm">Diese Ausschreibung wurde für keine Suchprofile bewertet.</span>
              )}
            </SidebarSection>

            <SidebarSection
              title={t('TenderSidebar_VoteSection_Title')}
              icon={faThumbsUp}
              isOpen={openSections.teamVotes}
              toggle={() => toggleSection('teamVotes')}
            >
              <TeamVotesSection votes={votes} />
            </SidebarSection>
            {isActive && hasDocuments && (
              <SidebarSection
                title="KI Chats"
                icon={faMessagesQuestion}
                isOpen={openSections.kiChats}
                toggle={() => toggleSection('kiChats')}
              >
                <ul className="text-sm">
                  <li className="mb-2">
                    <Button
                      icon={faCommentPlus}
                      label="Neue Anfrage"
                      locked={isQuestionLimitReached}
                      onClick={() => handleOpenChat()}
                      buttonClassName="min-h-9 h-9 w-full rounded text-left text-white hover:underline"
                      variant="secondary"
                    />
                  </li>
                  {recentChats.map((chat) => (
                    <li key={chat.id} className="mb-2 w-full rounded bg-base-200 p-2">
                      <button onClick={() => handleOpenChat(chat.id)} className="text-left hover:underline">
                        <div className="line-clamp-2">{chat.title}</div>
                      </button>
                    </li>
                  ))}
                  {showQuestionLimitCard && (
                    <li className="mb-[-16px]">
                      <SubscriptionLimitsCard type="QUESTIONS" />
                    </li>
                  )}
                </ul>
              </SidebarSection>
            )}

            <SidebarSection
              title="Notizen"
              icon={faMemoPad}
              isOpen={openSections.notesAndComments}
              toggle={() => toggleSection('notesAndComments')}
            >
              <CommentSection tenderId={tenderId} />
            </SidebarSection>
          </div>
        </div>
      </div>
      <AiChatModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        tenderId={tenderId}
        chatId={chatId}
        setChatId={setChatId}
        isLimitReached={isQuestionLimitReached}
      />
    </>
  );
};

export default TenderSidebar;
